import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Ticket, MapPin, CalendarDays, PartyPopper, ArrowRight, Music2, Users, Bed, Star } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../../components/Reveal";
import { Eyebrow, Button } from "../../components/Bits";
import ProjectGallery from "../../components/ProjectGallery";
import { IMG, GALLERIES } from "../../lib/content";
import { LINEUP, GRADS, TICKET_URL, EVENT_DATE, EVENT_LOCATION } from "./prideData";
import { LineupCard, Countdown, RainbowBar, RainbowArc, RAINBOW } from "./PrideParts";

const cards = [
  { to: "/pride/whats-on", icon: PartyPopper, title: "What's on", body: "The full day plan, from the parade to the main arena." },
  { to: "/pride/lineup", icon: Music2, title: "Main stage", body: "Meet the acts joining us on the main arena stage." },
  { to: "/pride/parade", icon: Users, title: "Pride parade", body: "Join the march through Whitehaven, everyone welcome." },
  { to: "/pride/visit", icon: Bed, title: "Visit", body: "Travel, parking and places to stay nearby." },
];

const headliners = LINEUP.filter((a) => a.headline);

export default function PrideHome() {
  return (
    <div>
      <section className="relative overflow-hidden bg-nead-ink text-white">
        <div className="absolute inset-0">
          <img src={IMG.pride} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-nead-ink via-nead-ink/85 to-nead-ink/55" />
        </div>
        <RainbowArc className="-top-40 -right-32 h-[28rem] w-[28rem]" />
        <RainbowArc className="-bottom-40 -left-40 h-96 w-96" />
        <div className="relative container-x pt-16 pb-20 md:pt-24 md:pb-28">
          <Eyebrow className="text-nead-cyan">Whitehaven Weekender &middot; Sunday</Eyebrow>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="mt-5 font-display font-bold tracking-tight text-4xl sm:text-6xl lg:text-[5rem] leading-[1.02] max-w-4xl">
            Pride by the <span className="bg-clip-text text-transparent" style={{ backgroundImage: RAINBOW }}>Harbourside</span>
          </motion.h1>
          <p className="mt-6 max-w-2xl text-lg text-white/75 leading-relaxed">
            Cumbria's biggest celebration of equality, diversity and belonging, a full day of live music, a colourful parade and community spirit right on Whitehaven Harbour.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/85">
            <span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4 text-nead-cyan" /> {EVENT_DATE}</span>
            <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-nead-cyan" /> {EVENT_LOCATION}</span>
            <span className="inline-flex items-center gap-2"><PartyPopper className="h-4 w-4 text-nead-cyan" /> Free entry</span>
          </div>
          <div className="mt-9"><Countdown /></div>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href={TICKET_URL} target="_blank" rel="noreferrer" data-testid="pride-hero-tickets" className="inline-flex items-center gap-2 rounded-full bg-nead-pink px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-nead-navy transition-colors">
              <Ticket className="h-4 w-4" /> Main arena tickets, £3.99
            </a>
            <Button to="/pride/lineup" variant="outline-light" testId="pride-hero-lineup">See the lineup</Button>
          </div>
        </div>
      </section>

      <RainbowBar />
      {/* Save the date marquee */}
      <div className="bg-nead-pink text-white py-3 overflow-hidden" data-testid="pride-savedate">
        <Marquee gradient={false} speed={44}>
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="mx-6 inline-flex items-center gap-3 font-display font-bold uppercase tracking-wide text-sm">
              Save the date <Star className="h-3.5 w-3.5 fill-white" /> Sunday 30 August 2026 <Star className="h-3.5 w-3.5 fill-white" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* Headliners */}
      <section className="container-x py-16 md:py-24">
        <Reveal>
          <Eyebrow className="text-nead-pink">Headline acts</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-nead-navy">Topping the bill this year.</h2>
          <div className="mt-4 h-1.5 w-28 rounded-full" style={{ backgroundImage: RAINBOW }} />
        </Reveal>
        <Stagger className="mt-10 grid sm:grid-cols-2 gap-6">
          {headliners.map((a) => (
            <StaggerItem key={a.name}>
              <div className="group relative aspect-[4/3] overflow-hidden rounded-[1.5rem] ring-2 ring-nead-pink" data-testid={`headliner-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <img src={a.photo} alt={a.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-nead-ink/90 via-nead-ink/20 to-transparent" />
                <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-nead-pink px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow">
                  <Star className="h-3.5 w-3.5 fill-white" /> Headliner
                </span>
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <h3 className="font-display font-bold text-3xl sm:text-4xl text-white tracking-tight">{a.name}</h3>
                  <p className="mt-1 text-white/70 text-sm">Live on the main arena stage</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Quick nav */}
      <section className="container-x pb-16 md:pb-20">
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c) => (
            <StaggerItem key={c.to}>
              <Link to={c.to} data-testid={`pride-card-${c.to.split("/").pop()}`} className="group block h-full rounded-[1.5rem] border border-black/[0.07] p-7 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                <div className="flex h-12 w-12 items-center justify-center rounded-full grad-pink-cyan"><c.icon className="h-6 w-6 text-white" strokeWidth={1.8} /></div>
                <h3 className="mt-5 font-display font-bold text-xl text-nead-navy">{c.title}</h3>
                <p className="mt-2 text-nead-navy/60 leading-relaxed text-[0.95rem]">{c.body}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-nead-pink">Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Lineup preview */}
      <section className="bg-nead-ink text-white">
        <div className="container-x py-20 md:py-24">
          <Reveal>
            <Eyebrow className="text-nead-cyan">On the main stage</Eyebrow>
            <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">Plus a whole lot more.</h2>
          </Reveal>
          <Stagger className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {LINEUP.slice(0, 8).map((a, i) => (
              <StaggerItem key={a.name}><LineupCard name={a.name} photo={a.photo} grad={GRADS[i % GRADS.length]} headline={a.headline} /></StaggerItem>
            ))}
          </Stagger>
          <div className="mt-10"><Button to="/pride/lineup" variant="pink" testId="pride-preview-more">See the full lineup</Button></div>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-nead-mist">
        <div className="container-x py-20 md:py-24">
          <Reveal><Eyebrow className="text-nead-pink">Gallery</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl tracking-tight text-nead-navy mb-10">Moments from Pride.</h2></Reveal>
          <ProjectGallery galleryKey="pride" fallback={GALLERIES.pride} label="Pride by the Harbourside" />
        </div>
      </section>
    </div>
  );
}
